import { useEffect, useState } from "react";
import { HasAccess } from "@/components/common/has-access";
import { useSearchParams } from "react-router-dom";
import { ActivitySquare, Link2Off, Loader2 } from "lucide-react";
import { ContentLayout } from "@/components/common/content-layout";
import CustomPaginationComponent from "@/components/common/custom-pagination-component";
import { UnmappedSessionsTable } from "@/features/audit/ui/unmapped-sessions-table";
import { UnmappedSessionsFilters } from "@/features/audit/ui/unmapped-sessions-filters";
import { useUnmappedSessions } from "@/features/audit/hooks/use-unmapped-sessions";
import { UserDetailsDrawer } from "@/features/users/ui/user-details-drawer";
import { AuditFixDrawer } from "@/features/audit/ui/audit-fix-drawer";
import { useActiveAuditFixRun, useStartAutoFix } from "@/features/audit/hooks/use-audit-fix";
import { Button } from "@/components/ui/button";

export default function UnmappedSessionsAudit() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isActivityOpen, setIsActivityOpen] = useState(false);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);

  const handleUserClick = (id: string) => {
    setSelectedUserId(id);
    setIsDrawerOpen(true);
  };

  const page = Number(searchParams.get("page") || 0);
  const search = searchParams.get("search") || undefined;
  const exclude = searchParams.get("exclude") || undefined;
  // Default: 2024-01-01 → today (as ISO strings, matching the calendar picker)
  const date_from = searchParams.get("date_from") || new Date("2024-01-01").toISOString();
  const date_to = (() => {
    const raw = searchParams.get("date_to");
    if (raw) return raw;
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    return end.toISOString();
  })();

  const { data, isLoading, isFetching, error } = useUnmappedSessions({
    page,
    search,
    exclude,
    date_from,
    date_to,
  });

  // Re-attach to any in-progress run on mount
  const { data: activeRunData } = useActiveAuditFixRun();
  useEffect(() => {
    if (activeRunData?.run) {
      setActiveRunId(activeRunData.run.id);
    }
  }, [activeRunData]);

  const startMutation = useStartAutoFix();

  const handleStartAutoFix = () => {
    startMutation.mutate(
      { date_from, date_to, exclude },
      { onSuccess: (d) => { setActiveRunId(d.run_id); setIsActivityOpen(true); } }
    );
  };

  const groups = data?.groups ?? [];
  const currentPage = data?.page ?? 0;
  const lastPage = data ? data.lastPage + 1 : 0;
  const isRunActive = !!activeRunData?.run &&
    ["PENDING", "RUNNING", "STOPPING"].includes(activeRunData.run.status);

  return (
    <>
    <UserDetailsDrawer
      userId={selectedUserId}
      isOpen={isDrawerOpen}
      onOpenChange={setIsDrawerOpen}
    />
    <AuditFixDrawer
      open={isActivityOpen}
      onOpenChange={setIsActivityOpen}
      runId={activeRunId}
    />
    <ContentLayout
      header={
        <>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">
              Unmapped Sessions
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Active sessions not linked to any bundle, grouped by invitee.
            </p>
          </div>

          <div className="flex items-center gap-2">
            {/* Activity log icon button */}
            <Button
              variant="outline"
              size="sm"
              className="relative"
              onClick={() => setIsActivityOpen(true)}
              title="Activity Log"
            >
              <ActivitySquare className="h-4 w-4" />
              {isRunActive && (
                <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-blue-500 border-2 border-white animate-pulse" />
              )}
            </Button>

            {/* Start / running indicator */}
            {isRunActive ? (
              <Button size="sm" variant="outline" disabled className="text-blue-600 border-blue-200">
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                Auto-fix running…
              </Button>
            ) : (
              <HasAccess permission="write">
              <Button
                size="sm"
                onClick={handleStartAutoFix}
                disabled={startMutation.isPending || groups.length === 0}
              >
                {startMutation.isPending ? (
                  <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Starting…</>
                ) : (
                  "Start Auto-Fix"
                )}
              </Button>
              </HasAccess>
            )}

            <UnmappedSessionsFilters />
          </div>
        </>
      }
      data={groups}
      isLoading={isLoading}
      isFetching={isFetching}
      error={error}
      errorMessage="Failed to load unmapped sessions"
      emptyState={{
        message: "No unmapped sessions found",
        icon: <Link2Off className="h-8 w-8 text-muted-foreground" />,
      }}
    >
      <UnmappedSessionsTable groups={groups} onUserClick={handleUserClick} />

      {groups.length > 0 && (
        <div className="mt-4">
          <CustomPaginationComponent
            currentPage={currentPage}
            lastPage={lastPage}
            onPageChange={(newPage) => {
              const params = new URLSearchParams(searchParams);
              params.set("page", newPage.toString());
              setSearchParams(params);
            }}
          />
        </div>
      )}
    </ContentLayout>
    </>
  );
}
